import React, { useState } from "react";
import LoginButton from "./LoginButton";

const CopyIcon = () => (
  <svg
    style={{ width: "14px", height: "14px" }}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    style={{ width: "14px", height: "14px" }}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "28px",
        height: "28px",
        backgroundColor: copied
          ? "#d1fae5"
          : isHovered
          ? "#f3f4f6"
          : "transparent",
        border: "none",
        borderRadius: "6px",
        color: copied ? "#047857" : "#6b7280",
        cursor: "pointer",
        transition: "all 0.2s ease",
      }}
      title="Copy to clipboard"
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
};

// User Card Component
const UserCard = ({ user }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        backgroundColor: "white",
        border: isHovered ? "1px solid #c4b5fd" : "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "20px",
        marginBottom: "12px",
        boxShadow: isHovered ? "0 4px 12px rgba(124, 58, 237, 0.1)" : "none",
        transition: "all 0.2s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Left: User Info */}
      <div style={{ flex: 1 }}>
        <h4
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#111827",
            margin: "0 0 12px 0",
          }}
        >
          {user.fullname || `${user.first_name} ${user.last_name}`}
        </h4>

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span
              style={{
                fontSize: "13px",
                color: "#9ca3af",
                width: "70px",
                flexShrink: 0,
              }}
            >
              email:
            </span>
            <span
              style={{
                fontSize: "13px",
                color: "#4b5563",
                fontFamily: "monospace",
                wordBreak: "break-all",
              }}
            >
              {user.email}
            </span>
            <CopyButton text={user.email} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span
              style={{
                fontSize: "13px",
                color: "#9ca3af",
                width: "70px",
                flexShrink: 0,
              }}
            >
              password:
            </span>
            <span
              style={{
                fontSize: "13px",
                color: "#4b5563",
                fontFamily: "monospace",
              }}
            >
              {user.password}
            </span>
            <CopyButton text={user.password} />
          </div>
        </div>
      </div>

      {/* Right: Action */}
      <LoginButton user={user} text="Log as" />
    </div>
  );
};

// Role Section Component
const RoleSection = ({ role, users }) => {
  const roleUsers = users.filter((u) => u.role === role);
  if (roleUsers.length === 0) return null;

  return (
    <div style={{ marginBottom: "24px" }}>
      {/* Role Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "16px",
        }}
      >
        <div style={{ height: "1px", flex: 1, backgroundColor: "#e5e7eb" }} />
        <span
          style={{
            fontSize: "13px",
            fontWeight: "600",
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {role}
        </span>
        <div style={{ height: "1px", flex: 1, backgroundColor: "#e5e7eb" }} />
      </div>

      {/* Users */}
      {roleUsers.map((user) => (
        <UserCard key={user.id || user.email} user={user} />
      ))}
    </div>
  );
};

export default function UsersList({ users }) {
  const roles = ["Account owner", "Administrator", "Controller", "Requester"];

  if (!users || users.length === 0) {
    return (
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "48px",
          border: "1px solid #e5e7eb",
          textAlign: "center",
          color: "#6b7280",
        }}
      >
        No users found
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "16px",
        padding: "28px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
      }}
    >
      {roles.map((role) => (
        <RoleSection key={role} role={role} users={users} />
      ))}
    </div>
  );
}
